import { trans } from '@elements/translation';
import type { GoogleAddressResult } from '@global-types/googleAddress.ts';
import type { DealUdurMarker } from '@models/dealUdurMarker.ts';
import { flashBagSuccess } from '@tools/flashbag';
import { Logger } from '@tools/logger/logger';
import { defineStore } from 'pinia';
import { useModal } from '@composables/useModal.ts';
import { ref, computed } from 'vue';
import { createDealUdurMarker, deleteDealUdurMarker } from '@api/dealUdur/marker/apiDealUdurMarker';
import { useLoading } from '@composables/useLoading.ts';
import type { SelectionItemInitialAppState } from '@global-types/initialAppState.ts';

export const useCustomMarkerStore = defineStore('custom-marker-store', () => {
  const { isOpen, openModal: open, closeModal: close } = useModal();

  const savedDealUdurMarker = ref<DealUdurMarker | null>((window.selectionItemInitialAppState as SelectionItemInitialAppState).deal_udur_marker);
  const dealUdurMarker = ref<DealUdurMarker | null>(savedDealUdurMarker.value);

  const { isLoading, startLoading, stopLoading } = useLoading();

  const googleAddress = computed<GoogleAddressResult | null>({
    get: () => {
      if (!dealUdurMarker.value) {
        return null;
      }
      return {
        placeId: dealUdurMarker.value.place_id,
        text: dealUdurMarker.value.label,
        location: {
          latitude: dealUdurMarker.value.latitude,
          longitude: dealUdurMarker.value.longitude,
        },
      } as GoogleAddressResult;
    },
    set(value: GoogleAddressResult | null) {
      dealUdurMarker.value = {
        ...dealUdurMarker.value,
        place_id: value?.placeId,
        label: value?.text,
        latitude: value?.location.latitude,
        longitude: value?.location.longitude,
      } as DealUdurMarker;
    },
  });

  const addressName = computed<string | null>({
    get: () => dealUdurMarker.value?.name ?? null,
    set(value: string | null) {
      dealUdurMarker.value = {
        ...dealUdurMarker.value,
        name: value,
      } as DealUdurMarker;
    },
  });

  const save = async (): Promise<void> => {
    startLoading();

    let isSaved: boolean;

    if (!dealUdurMarker.value?.place_id) {
      dealUdurMarker.value = null;
      isSaved = await deleteDealUdurMarker();
    } else {
      isSaved = await createDealUdurMarker(dealUdurMarker.value);
    }

    if (!isSaved) {
      Logger.error('Error while saving marker');
      stopLoading();
      return;
    }

    flashBagSuccess(trans(savedDealUdurMarker.value ? 'selection_item.custom_marker.success.updated' : 'selection_item.custom_marker.success.created'));

    savedDealUdurMarker.value = dealUdurMarker.value;

    close();

    stopLoading();
  };

  return {
    isOpen,
    isSaving: isLoading,
    open,
    close,
    save,
    dealUdurMarker,
    savedDealUdurMarker,
    googleAddress,
    addressName,
  };
});
